import type { RouterProps } from '@lmc/common/types'
import {
  type Carer,
  type Home,
  Log,
  type ResidentItem,
  type User
} from '@lmc/ts-common'
import { Row } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import useHasHomesList from 'components/AccessControl/useHasHomesList'
import { Spinner } from 'components/generic'
import { isNoticeboardGranted } from 'components/navigation/utils'
import useHomeType from 'hooks/useHomeType'
import { type FC, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MonitoringHomeCard from 'screens/Rostering/Monitoring/HomeCard/MonitoringHomeCard'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import Footer from '../../LEGACY/Footer'
import theme from '../../LEGACY/theme'
import Operations from '../../operations'
import { history as router } from '../../store'
import AdvertCard from './components/AdvertCard'
import BirthdaysCard from './components/BirthdaysCard'
import Card from './components/Card'
import { CardSkeleton } from './components/CardSkeleton'
import CarePlansCard from './components/CarePlansCard'
import CarersCard from './components/CarersCard'
import { CaseIncidentsCard } from './components/CaseIncidentsCard'
import FamilyCard from './components/FamilyCard'
import GoalsCard from './components/GoalsCard'
import HomeTitle from './components/HomeTitle'
import IncidentsCard from './components/IncidentsCard'
import MedicationCard from './components/MedicationCard'
import MedicationCardV2 from './components/MedicationCardV2'
import NoticeBanner from './components/NoticeBanner'
import { OnboardingCard } from './components/OnboardingCard'
import ResidentsCard from './components/ResidentsCard'
import TodosCard from './components/TodosCard'
import {
  type FlagFetchStatus,
  readCachedHomeFlags,
  resolveCardSlot,
  writeCachedHomeFlags
} from './homeGridFlags'
import { useIntroStep } from './hooks'

type HomeExtended = Home & { onTrial?: boolean }

const HomeScreen: FC = () => {
  const {
    data: flags,
    isSuccess: flagsSuccess,
    isError: flagsError
  } = useFeatureFlags({
    flagNames: [
      'incident_reports_home_card',
      'outcomes_and_goals_v2',
      'incident_case_action_assignment',
      'emar_v2_stock'
    ]
  })

  const { hasRota, isPro: hasPro } = useHomeType()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasMounted = useRef(false)

  const dispatch = useDispatch()

  const fetchData = () => {
    dispatch(Operations.loadList('member-benefits'))
  }

  const showNoticeboard = isNoticeboardGranted(features)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchData()
  }, [])

  const fetchHomeData = (homeId: string) => {
    dispatch(Operations.getHomeDetails(homeId))
  }

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const categories = useSelector(
    (state: { data: { [`log-categories`]: object } }) =>
      state.data['log-categories']
  )

  const memberBenefits = useSelector(
    (state: { data: { [`member-benefits`]: object } }) =>
      state.data['member-benefits']
  )

  const residents = useSelector(
    (state: { data: { residents: ResidentItem[] } }) => state.data.residents
  )

  const exampleResidentId = useMemo(
    () => residents?.find((resident) => resident.status === 'dummy')?.id,
    [residents]
  )

  const canManageHome = useAbilityAccess([{ i: 'update', this: 'Home' }])
  const isGroupUser = useHasHomesList()
  const abilitiesLoaded = useSelector(
    (state: { global: { ability: any } }) => !!state.global.ability
  )

  const { step, qrCode, url } = useIntroStep(user?.id, canManageHome)

  const hasCarePlans = home && home.carePlans

  const hasTrial = home && (home as HomeExtended)?.onTrial

  const hasFamilySetupComplete = home && home?.familySetupComplete

  const hasEmar = home && home?.eMAR

  const hasFamilyModuleEnabled = home && home?.family

  const showFamilyAdvert = hasFamilyModuleEnabled && !hasFamilySetupComplete

  // CM-3140: feature flags resolve after first paint, so flag-gated cards would
  // otherwise pop in and reflow the grid. Cache last-known flags per home so a
  // returning load paints the final layout immediately, and only fall back to a
  // skeleton on a true cold load. A failed fetch keeps the cache untouched and
  // falls back to it rather than the all-disabled default. See homeGridFlags.ts.
  const flagsStatus: FlagFetchStatus = flagsSuccess
    ? 'success'
    : flagsError
      ? 'error'
      : 'loading'
  const cachedFlags = useMemo(() => readCachedHomeFlags(home?.id), [home?.id])

  const incidentsSlot = resolveCardSlot({
    preconditionMet: Boolean(
      features?.features?.incidentReporting?.enabled && hasPro
    ),
    liveFlagEnabled: Boolean(flags.incident_reports_home_card.enabled),
    status: flagsStatus,
    cachedFlagEnabled: cachedFlags?.incidentReports
  })

  const goalsSlot = resolveCardSlot({
    preconditionMet: Boolean(home?.features?.goals?.enabled),
    liveFlagEnabled: Boolean(flags.outcomes_and_goals_v2.enabled),
    status: flagsStatus,
    cachedFlagEnabled: cachedFlags?.goals
  })

  const showMedicationV2 = flagsSuccess
    ? Boolean(flags.emar_v2_stock?.enabled)
    : (cachedFlags?.emarV2 ?? false)

  useEffect(() => {
    // Only persist a genuine success — a failed fetch exposes all-disabled
    // defaults that would otherwise clobber the cached values for this home.
    if (!flagsSuccess || !home?.id) {
      return
    }
    writeCachedHomeFlags(home.id, {
      incidentReports: Boolean(flags.incident_reports_home_card.enabled),
      goals: Boolean(flags.outcomes_and_goals_v2.enabled),
      emarV2: Boolean(flags.emar_v2_stock?.enabled)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flagsSuccess,
    home?.id,
    flags.incident_reports_home_card.enabled,
    flags.outcomes_and_goals_v2.enabled,
    flags.emar_v2_stock?.enabled
  ])

  const showAdvertCard =
    !hasPro || !hasEmar || !hasFamilyModuleEnabled || showFamilyAdvert
  const showOnboardingCard = hasPro && Number(step) < 6

  const isLoading = !carers || !categories || !memberBenefits

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    fetchHomeData(home?.id)
  }, [])

  const renderCaseIncidentsSlot = (cardId?: string) =>
    incidentsSlot === 'hide' ? null : (
      <Card id={cardId}>
        {incidentsSlot === 'skeleton' ? (
          <CardSkeleton />
        ) : (
          <CaseIncidentsCard />
        )}
      </Card>
    )

  const renderGoalsSlot = () =>
    goalsSlot === 'hide' ? null : (
      <Card>{goalsSlot === 'skeleton' ? <CardSkeleton /> : <GoalsCard />}</Card>
    )

  const renderSeniorCarer = () => (
    <div style={styles.container} className="row">
      <div>
        <div className="row">
          <div className="twelve columns">
            <HomeTitle />
          </div>
        </div>
        <div id="intro-js-step-dashboard">
          <Row gutter={18}>
            <Card id="intro-js-step-residents">
              <ResidentsCard residents={residents} />
            </Card>
            {renderCaseIncidentsSlot('intro-js-step-case-incidents')}
            <Card id="intro-js-step-incidents">
              <IncidentsCard hasPro={hasPro} hasTrial={hasTrial} />
            </Card>
            <Card id="intro-js-step-tasks">
              <TodosCard />
            </Card>
            {hasRota && hasPro && (
              <Card>
                <MonitoringHomeCard />
              </Card>
            )}
            {hasCarePlans && (
              <Card>
                <CarePlansCard
                  isLoadingProp={isLoading}
                  router={router as unknown as RouterProps}
                />
              </Card>
            )}
            <Card>
              <BirthdaysCard
                isLoading={isLoading}
                carers={carers}
                residents={residents}
              />
            </Card>
          </Row>
        </div>
      </div>
    </div>
  )

  if (!abilitiesLoaded) {
    return <Spinner />
  }

  if (!canManageHome && !isGroupUser) {
    return <div>{renderSeniorCarer()}</div>
  }

  return (
    <>
      <div style={{ padding: '0 20px' }}></div>
      <div style={styles.container} className="row">
        {showNoticeboard && <NoticeBanner />}
        <div>
          <div className="row">
            <div className="twelve columns">
              <HomeTitle user={user} />
            </div>
          </div>
          <div id="intro-js-step-dashboard">
            <Row gutter={18}>
              <Card id="intro-js-step-residents">
                <ResidentsCard residents={residents} />
              </Card>
              {renderCaseIncidentsSlot('intro-js-step-residents')}
              <Card id="intro-js-step-setup">
                <IncidentsCard hasPro={hasPro} hasTrial={hasTrial} />
              </Card>
              <Card id="intro-js-step-carers">
                <CarersCard isLoading={isLoading} carers={carers} />
              </Card>
              <Card id="intro-js-step-tasks">
                <TodosCard hasPro={hasPro} />
              </Card>
              {hasRota && hasPro && (
                <Card>
                  <MonitoringHomeCard />
                </Card>
              )}
              {hasCarePlans && (
                <Card>
                  <CarePlansCard
                    isLoadingProp={isLoading}
                    homeID={home?.id}
                    router={router as unknown as RouterProps}
                    hasPro={hasPro}
                  />
                </Card>
              )}
              {showOnboardingCard && (
                <Card>
                  <OnboardingCard
                    carerId={user?.id}
                    exampleResidentId={exampleResidentId}
                    step={step}
                    qrCode={qrCode}
                    url={url}
                  />
                </Card>
              )}
              {home.eMAR && (
                <Card>
                  {showMedicationV2 ? <MedicationCardV2 /> : <MedicationCard />}
                </Card>
              )}

              {renderGoalsSlot()}
              {home.onboardingCompleted && (
                <Card>
                  <FamilyCard />
                </Card>
              )}
              {showAdvertCard && <AdvertCard />}
              <Card>
                <BirthdaysCard
                  isLoading={isLoading}
                  carers={carers}
                  residents={residents}
                />
              </Card>
            </Row>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

const styles = {
  container: {
    padding: '20px',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: theme.container.size.large,
    minHeight: 'calc(100vh - 63px)'
  }
}

export default HomeScreen
